/* Hopla — global behaviour. No dependencies. */
(() => {
  const HP = (window.HP = window.HP || {});
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = window.Shopify?.routes?.root || '/';

  /* ---------- language + money (snippets/hp-i18n.liquid) ---------- */
  const I18N = window.HP_I18N || {};
  HP.t = (key, vars = {}) => (I18N.t?.[key] ?? key).replace(/%(\w+)%/g, (_, k) => vars[k] ?? '');
  const M = I18N.money;
  const currency = window.Shopify?.currency?.active || 'EUR';
  const fmt = new Intl.NumberFormat(currency === 'CHF' ? 'fr-CH' : 'fr-FR', { style: 'currency', currency });
  HP.money = (cents) => {
    const value = (Number(cents) || 0) / 100;
    if (!M) return fmt.format(value);
    const [whole, frac] = value.toFixed(2).split('.');
    return `${M.prefix}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, M.group)}${M.decimal}${frac}${M.suffix}`;
  };

  /* ---------- physics (damping ratio + response, as in UIKit springs) ---------- */
  const spring = (from, to, velocity, { damping = 1, response = 0.35 } = {}, onFrame, onDone) => {
    const k = ((2 * Math.PI) / response) ** 2;
    const c = (4 * Math.PI * damping) / response;
    let x = from - to;
    let v = velocity;
    let last = performance.now();
    let raf = requestAnimationFrame(function step(now) {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const n = Math.max(1, Math.ceil(dt / 0.004));
      for (let i = 0; i < n; i++) {
        v += (-k * x - c * v) * (dt / n);
        x += v * (dt / n);
      }
      if (Math.abs(x) < 0.5 && Math.abs(v) < 10) {
        onFrame(to);
        onDone?.();
        return;
      }
      onFrame(to + x);
      raf = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(raf);
  };
  // where a flick would come to rest (scroll-view deceleration)
  const project = (velocity, rate = 0.998) => ((velocity / 1000) * rate) / (1 - rate);
  const rubberband = (over, size, c = 0.55) => (over * size * c) / (size + c * Math.abs(over));
  HP.spring = spring;

  /* ---------- toast ---------- */
  let toastEl;
  let toastTimer;
  HP.toast = (msg) => {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'hp-toast';
      toastEl.setAttribute('role', 'status');
      document.body.append(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 2600);
  };

  /* ---------- reveal on scroll (content stays visible without JS) ---------- */
  const AUTO_REVEAL = [
    '.hp-bento__head', '.hp-bento > .hp-tile5', '.hp-prail .hp-rail', '.hp-giftx__head', '.hp-giftx__media', '.hp-giftx .hp-index li',
    '.hp-annot__body > *', '.hp-seq', '.hp-faq-sec .hp-acc', '.hp-quote__line', '.hp-quote__media', '.hp-comm__invite > *',
    '.hp-ideas__rail', '.hp-reviews__title', '.hp-bleed__label',
  ].join(', ');
  const initReveal = (scope = document) => {
    scope.querySelectorAll(AUTO_REVEAL).forEach((el) => el.setAttribute('data-hp-reveal', ''));
    const els = [...scope.querySelectorAll('[data-hp-reveal]:not(.is-in)')];
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    // once shown, an element gets its own transitions back (press feedback must stay instant)
    const done = (el, delay) => setTimeout(() => el.removeAttribute('data-hp-reveal'), delay);
    const vh = innerHeight;
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) {
        el.classList.add('is-in');
        done(el, 0);
      }
      const siblings = [...(el.parentElement?.children || [])].filter((c) => c.hasAttribute('data-hp-reveal'));
      el.style.setProperty('--hp-i', String(Math.min(siblings.indexOf(el), 5)));
    });
    document.documentElement.classList.add('hp-js');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
            done(e.target, 900 + Number(e.target.style.getPropertyValue('--hp-i') || 0) * 70);
          }
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
    );
    els.forEach((el) => !el.classList.contains('is-in') && io.observe(el));
  };
  HP.reveal = initReveal;

  /* ---------- rails ---------- */
  const initRails = (scope = document) => {
    scope.querySelectorAll('[data-hp-rail-nav]').forEach((nav) => {
      if (nav.dataset.bound) return;
      nav.dataset.bound = '1';
      const rail = document.getElementById(nav.dataset.hpRailNav);
      if (!rail) return;
      const [prev, next] = nav.querySelectorAll('button');
      const step = () => (rail.firstElementChild?.getBoundingClientRect().width || 300) + 16;
      const sync = () => {
        prev.disabled = rail.scrollLeft < 4;
        next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      };
      prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: reduce ? 'auto' : 'smooth' }));
      next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: reduce ? 'auto' : 'smooth' }));
      rail.addEventListener('scroll', sync, { passive: true });
      sync();
    });
  };

  /* ---------- cart ---------- */
  const drawer = () => document.getElementById('hp-cart');
  let opener = null;

  const updateBubbles = (count) => {
    document.querySelectorAll('.cart-bubble').forEach((b) => {
      b.classList.toggle('visually-hidden', !count);
      const n = b.querySelector('.cart-bubble__text-count');
      if (n) {
        n.textContent = count > 99 ? '99+' : String(count);
        n.classList.toggle('hidden', !count);
      }
    });
  };

  HP.refreshCart = async (html) => {
    const d = drawer();
    if (!d) return;
    let markup = html;
    if (!markup) {
      const res = await fetch(`${location.pathname}?sections=hp-cart-drawer`, { headers: { Accept: 'application/json' } });
      markup = (await res.json())['hp-cart-drawer'];
    }
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const fresh = doc.querySelector('.hp-drawer__inner');
    const current = d.querySelector('.hp-drawer__inner');
    if (fresh && current) {
      const scroll = current.querySelector('.hp-drawer__body')?.scrollTop || 0;
      current.replaceWith(fresh);
      const body = fresh.querySelector('.hp-drawer__body');
      if (body) body.scrollTop = scroll;
      updateBubbles(Number(fresh.dataset.hpCartCount || 0));
      if (d.open) fitCartPreviews();
    }
  };

  // a closed dialog has no width, so the names on the cart previews are fitted once it is shown
  const fitCartPreviews = () => requestAnimationFrame(() => drawer()?.querySelectorAll('[data-hp-preview]').forEach((p) => HP.fitName?.(p)));

  HP.openCart = (trigger) => {
    const d = drawer();
    if (!d) {
      location.href = `${root}cart`;
      return;
    }
    opener = trigger || document.activeElement;
    if (!d.open) d.showModal();
    d.querySelector('.hp-drawer__inner')?.focus({ preventScroll: true });
    fitCartPreviews();
  };
  HP.closeCart = () => {
    const d = drawer();
    if (d?.open) d.close();
  };

  /** Add lines to the cart. items: [{ id, quantity, properties }] */
  HP.addToCart = async (items, { open = true } = {}) => {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: 'hp-cart-drawer', sections_url: location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || HP.t('addError'));
    await HP.refreshCart(data.sections?.['hp-cart-drawer']);
    drawer()?.querySelector('[data-hp-cart-added]')?.removeAttribute('hidden');
    if (open) HP.openCart();
    document.dispatchEvent(new CustomEvent('hp:cart-added', { detail: data }));
    return data;
  };

  const changeLine = async (line, quantity) => {
    const inner = drawer()?.querySelector('.hp-drawer__inner');
    inner?.classList.add('is-busy');
    try {
      const res = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: 'hp-cart-drawer', sections_url: location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || HP.t('updateError'));
      await HP.refreshCart(data.sections?.['hp-cart-drawer']);
    } catch (e) {
      HP.toast(e.message);
      inner?.classList.remove('is-busy');
    }
  };

  document.addEventListener('click', async (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;

    const cartLink = t.closest('a.action__cart, a[href$="/cart"][data-hp-open-cart]');
    if (cartLink && drawer() && !document.body.classList.contains('template-cart')) {
      e.preventDefault();
      HP.openCart(cartLink);
      return;
    }
    if (t.closest('[data-hp-cart-close]')) {
      HP.closeCart();
      return;
    }
    const qty = t.closest('[data-hp-qty]');
    if (qty) {
      const line = Number(qty.closest('[data-line]')?.dataset.line);
      if (line) changeLine(line, Number(qty.dataset.hpQty));
      return;
    }
    const up = t.closest('[data-hp-upsell]');
    if (up) {
      up.classList.add('is-loading');
      try {
        await HP.addToCart([{ id: Number(up.dataset.hpUpsell), quantity: 1 }], { open: true });
      } catch (err) {
        HP.toast(err.message);
        up.classList.remove('is-loading');
      }
    }
  });

  document.addEventListener('click', (e) => {
    const d = drawer();
    if (d?.open && e.target === d) HP.closeCart(); // backdrop click
  });

  document.addEventListener('close', (e) => {
    if (e.target === drawer() && opener instanceof HTMLElement) opener.focus({ preventScroll: true });
  }, true);

  /* ---------- home personalisation demo ---------- */
  const fitName = (preview) => {
    const nameEl = preview.querySelector('.hp-preview__name');
    const box = preview.querySelector('.hp-preview__text');
    if (!nameEl || !box) return;
    nameEl.style.setProperty('--fit', '1');
    const natural = nameEl.parentElement.scrollWidth;
    const avail = box.clientWidth * 0.94;
    if (natural > avail && natural > 0) nameEl.style.setProperty('--fit', String(Math.max(0.45, avail / natural)));
  };
  HP.fitName = fitName;

  const initDemo = (scope = document) => {
    scope.querySelectorAll('[data-hp-demo]').forEach((demo) => {
      if (demo.dataset.bound) return;
      demo.dataset.bound = '1';
      const preview = demo.querySelector('[data-hp-preview]');
      const nameIn = demo.querySelector('[data-hp-demo-name]');
      const roleIn = demo.querySelector('[data-hp-demo-role]');
      const cta = demo.querySelector('[data-hp-demo-cta]');
      if (!preview) return;
      let url = demo.querySelector('.hp-demo__swatch[aria-checked="true"]')?.dataset.url || cta?.getAttribute('href')?.split('#')[0];
      const update = () => {
        const name = nameIn?.value.trim() || nameIn?.dataset.default || HP.t('yourName');
        const role = roleIn?.value || '';
        preview.querySelector('[data-hp-preview-name]').textContent = name;
        preview.querySelector('[data-hp-preview-role]').textContent = role.toUpperCase();
        fitName(preview);
        if (cta && url) {
          const q = new URLSearchParams();
          if (nameIn?.value.trim()) q.set('prenom', nameIn.value.trim());
          if (role) q.set('metier', role);
          cta.href = `${url}${q.toString() ? `?${q}` : ''}#creer`;
        }
      };
      nameIn?.addEventListener('input', update);
      roleIn?.addEventListener('change', update);
      const modelOut = demo.querySelector('[data-hp-demo-model]');
      demo.querySelectorAll('.hp-demo__swatch').forEach((sw) =>
        sw.addEventListener('click', () => {
          demo.querySelectorAll('.hp-demo__swatch').forEach((o) => o.setAttribute('aria-checked', String(o === sw)));
          url = sw.dataset.url;
          if (modelOut && sw.dataset.title) modelOut.textContent = sw.dataset.title;
          const img = preview.querySelector('.hp-preview__img');
          if (img && sw.dataset.blank) {
            preview.classList.add('is-swapping');
            img.onload = () => requestAnimationFrame(() => preview.classList.remove('is-swapping'));
            img.removeAttribute('srcset');
            img.src = sw.dataset.blank;
          }
          preview.style.setProperty('--ink', sw.dataset.ink || '#ffffff');
          update();
        })
      );
      update();
      document.fonts?.ready.then(update);
    });
  };

  /* ---------- annotated photos: pins appear once the photo is in view ---------- */
  const initAnnot = (scope = document) => {
    const els = [...scope.querySelectorAll('[data-hp-annot]:not(.is-in)')];
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) return els.forEach((el) => el.classList.add('is-in'));
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    }), { threshold: 0.35 });
    els.forEach((el) => io.observe(el));
  };

  /* ---------- cut-out products drift a few pixels against the scroll ---------- */
  const initFloat = () => {
    if (reduce) return;
    let items = [];
    let ticking = false;
    const collect = () => { items = [...document.querySelectorAll('[data-hp-float]')]; };
    const frame = () => {
      ticking = false;
      const vh = innerHeight;
      items.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) return;
        const k = Number(el.dataset.hpFloat) || 0.05;
        el.style.setProperty('--hp-drift', `${((r.top + r.height / 2 - vh / 2) * -k).toFixed(1)}px`);
      });
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(frame); } };
    collect();
    addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('shopify:section:load', () => { collect(); frame(); });
    frame();
  };

  /* ---------- segmented controls: one thumb slides to the chosen option ---------- */
  HP.segThumb = (el, selected) => {
    if (!el || el.dataset.thumb) return;
    el.dataset.thumb = '1';
    el.classList.add('hp-thumbed', 'is-instant');
    const update = () => {
      const items = [...el.children].filter((c) => c.offsetParent !== null);
      const pick = el.querySelector(selected);
      const item = pick && items.find((i) => i.contains(pick) || i === pick);
      const row = items.length > 1 && items.every((i) => i.offsetTop === items[0].offsetTop);
      el.classList.toggle('is-row', Boolean(item && row));
      if (!item || !row) return;
      el.style.setProperty('--tx', `${item.offsetLeft}px`);
      el.style.setProperty('--ty', `${item.offsetTop}px`);
      el.style.setProperty('--tw', `${item.offsetWidth}px`);
      el.style.setProperty('--th', `${item.offsetHeight}px`);
    };
    el.addEventListener('change', update);
    el.addEventListener('hp:seg', update);
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => {
        el.classList.add('is-instant');
        update();
        requestAnimationFrame(() => requestAnimationFrame(() => el.classList.remove('is-instant')));
      }).observe(el);
    }
    update();
  };

  /* ---------- cart drawer (phones): swipe it back to the right; it follows the finger and keeps its momentum ---------- */
  const initSheet = () => {
    const d = drawer();
    if (!d || d.dataset.sheet) return;
    d.dataset.sheet = '1';
    const phone = matchMedia('(max-width: 749px)');
    let drag = null;
    let stop = null;
    let quietUntil = 0;
    // the click that follows a drag would otherwise press the button under the finger or read as a backdrop tap
    d.addEventListener('click', (e) => {
      if (performance.now() < quietUntil) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    }, true);
    const set = (x, w) => {
      d.style.transform = `translate3d(${x}px, 0, 0)`;
      d.style.setProperty('--sheet-open', String(Math.max(0, Math.min(1, 1 - x / w))));
    };
    const settle = () => {
      d.style.removeProperty('transform');
      d.style.removeProperty('transition');
      d.style.removeProperty('--sheet-open');
    };
    d.addEventListener('pointerdown', (e) => {
      if (!phone.matches || !d.open || e.button > 0 || e.target === d) return;
      if (e.target.closest('input, select, textarea')) return;
      stop?.();
      stop = null;
      const x = new DOMMatrixReadOnly(getComputedStyle(d).transform).m41 || 0; // grab it where it is on screen
      drag = { id: e.pointerId, origin: e.clientX - x, startY: e.clientY, w: d.offsetWidth, x, active: false, samples: [[e.timeStamp, x]] };
    });
    d.addEventListener('pointermove', (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const raw = e.clientX - drag.origin;
      if (!drag.active) {
        const dx = raw - drag.x;
        const dy = e.clientY - drag.startY;
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          drag = null; // a vertical move is a scroll through the cart
          return;
        }
        drag.active = true;
        d.setPointerCapture(e.pointerId);
        d.style.transition = 'none';
      }
      drag.x = raw >= 0 ? raw : -rubberband(-raw, drag.w);
      drag.samples.push([e.timeStamp, drag.x]);
      if (drag.samples.length > 5) drag.samples.shift();
      set(drag.x, drag.w);
    });
    const end = (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const { w, x, samples, active } = drag;
      drag = null;
      if (!active) return;
      quietUntil = performance.now() + 300;
      // only the last 100 ms count: a finger that paused before lifting has no momentum
      const recent = samples.filter(([t]) => e.timeStamp - t < 100);
      const [t0, x0] = recent[0] || [0, 0];
      const [t1, x1] = recent[recent.length - 1] || [0, 0];
      const velocity = recent.length > 1 && t1 > t0 ? ((x1 - x0) / (t1 - t0)) * 1000 : 0;
      const dismiss = x + project(velocity) > w * 0.5;
      if (reduce) {
        settle();
        if (dismiss) HP.closeCart();
        return;
      }
      const target = dismiss ? w : 0;
      stop = spring(x, target, velocity, dismiss ? { damping: 1, response: 0.3 } : { damping: 0.82, response: 0.32 }, (v) => set(v, w), () => {
        stop = null;
        if (dismiss) d.close();
        else settle();
      });
    };
    d.addEventListener('pointerup', end);
    d.addEventListener('pointercancel', end);
    d.addEventListener('close', () => {
      stop?.();
      stop = null;
      settle();
    });
  };

  /* ---------- links typed in content ("/pages/contact") keep the visitor's language prefix ("/en/pages/contact") ---------- */
  const localizeLinks = (scope = document) => {
    if (root === '/') return;
    const prefix = root.replace(/\/$/, '');
    scope.querySelectorAll('a[href^="/"]').forEach((a) => {
      const href = a.getAttribute('href');
      if (href === prefix || href.startsWith(`${prefix}/`)) return;
      if (!/^\/(collections|products|pages|policies|blogs|cart|search|account)(\/|\?|#|$)/.test(href)) return;
      a.setAttribute('href', prefix + href);
    });
  };

  /* ---------- language + currency: a tap applies the choice; keyboard users confirm with the Apply button ---------- */
  document.addEventListener('keydown', (e) => {
    const form = e.target instanceof Element ? e.target.closest('.hp-locale__form') : null;
    if (form && e.key !== 'Escape') form.classList.add('is-keyed');
    if (e.key !== 'Escape') return;
    document.querySelectorAll('details[data-hp-locale][open]').forEach((d) => {
      d.open = false;
      d.querySelector('summary')?.focus();
    });
  }, true);
  document.addEventListener('change', (e) => {
    const form = e.target instanceof Element ? e.target.closest('.hp-locale__form') : null;
    if (form && !form.classList.contains('is-keyed')) form.submit();
  });
  document.addEventListener('click', (e) => {
    document.querySelectorAll('details[data-hp-locale][open]').forEach((d) => {
      if (!d.contains(e.target)) d.open = false;
    });
  });

  /* ---------- init ---------- */
  const init = () => {
    document.documentElement.classList.add('hp-js');
    document.addEventListener('touchstart', () => {}, { passive: true }); // lets iOS Safari show :active on press
    localizeLinks();
    initSheet();
    initReveal();
    initRails();
    initDemo();
    initAnnot();
    initFloat();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', (e) => {
    localizeLinks(e.target);
    initReveal(e.target);
    initRails(e.target);
    initDemo(e.target);
    initAnnot(e.target);
  });
})();
